import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  ExecutionSource,
  Metadata,
  MetadataColumnsType,
  QueryType,
  OnValidateQueryRecordsSubscription,
  OnValidateQueryRecordsSubscriptionVariables,
  ValidationStatus,
  ValidateQueryRecordMutation,
  ValidateQueryRecordMutationVariables,
  QueryValidationRecord,
} from '../../../../API';
import { MonacoEditorComponent } from '../../../shared/components/monaco-editor/monaco-editor.component';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS } from '../../../shared/utils/constants';
import { QueryRecord } from '../../models/market-fan-exploration.model';
import { FanExplorationService } from '../../services/fan-exploration.service';
import { Router } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { ValidateQueryRecord } from '../../../../graphql/mutations';
import { Subscription } from 'rxjs';
import { onValidateQueryRecords } from '../../../../graphql/subscriptions';
import { ValidationResponse } from '../../../shared/models/query-interface';

interface DataBaseTableItem {
  datasourceName: string;
  databaseName: string;
  tableDescription: string;
  tableName: string;
  columns: MetadataColumnsType[];
}

@Component({
  selector: 'app-custom-query',
  templateUrl: './custom-query.component.html',
  styleUrl: './custom-query.component.scss',
})
export class CustomQueryComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('queryEditor') queryEditor!: MonacoEditorComponent;
  @Input() queryRecord: QueryRecord | null = null;
  @Output() historyTabRequested = new EventEmitter<void>();

  //validationLogs = 'Validation Logs will be here';
  validationLogs?: ValidationResponse;
  isQueryExpanded = false;
  isSavingDraft = false;
  isSubmittingQuery = false;
  isMetadataLoading = false;
  metadataErrorMessage = '';
  queryId = '';
  executionId = '';
  isLoading = false;
  spinningState: 'validate' | 'submit' | null = null;
  isValidating = false;
  validationStatus: ValidationStatus | null | undefined;
  checkValidationStatus = false;
  validationException?: string;
  orgId = '';
  queryValidationRecord?: QueryValidationRecord;

  customQueryFormGroup!: FormGroup;
  dataSourceList: string[] = [];
  tableMap = new Map<string, DataBaseTableItem[]>();
  dataSourceToDatabasesMap = new Map<string, string[]>();
  tableList: DataBaseTableItem[] = [];
  databaseList: string[] = [];
  selectedDatasources = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required],
  });
  selectedDatabases = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required],
  });
  previousDatasourcesValue: string[] = [];
  graphqlValidationSubscription?: Subscription;
  isGlossaryVisible = false;
  selectedTable: DataBaseTableItem | null = null;
  searchText = '';

  readonly noMetadataMessage = 'No schema metadata available for this organization.';

  constructor(
    private fb: FormBuilder,
    private fanExplorationService: FanExplorationService,
    private messageService: NzMessageService,
    private userManagementService: UserManagementService,
    private router: Router,
    private apiService: ApiService,
  ) {
    this.customQueryFormGroup = this.fb.nonNullable.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      description: ['', [Validators.required, Validators.maxLength(500), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
      query: [
        '--Write your SQL query here',
        [Validators.required, Validators.maxLength(40000), Validators.pattern(AT_LEAST_ONE_ALPHABET)],
      ],
      createdBy: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.graphqlValidationSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.orgId = this.userManagementService.getOrgId();
    this.customQueryFormGroup = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(45),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      description: ['', [Validators.required, Validators.maxLength(500), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
      query: ['', [Validators.required, Validators.maxLength(40000), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
    });

    this.applyQueryRecord();
    this.subscribeToMetadataSelectionChanges();
    void this.loadMetadata();
    this.listenRealTimeValidationChanges(this.orgId);
    this.subscribeToQueryRecordFormChangeEvent();

    this.customQueryFormGroup.valueChanges.subscribe(() => {
      this.isValidating = false;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['queryRecord'] && this.customQueryFormGroup) {
      this.applyQueryRecord();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // Escape key to collapse when expanded
    if (event.key === 'Escape' && this.isQueryExpanded) {
      this.toggleQueryExpanded();
      event.preventDefault();
    }
  }
  toggleQueryExpanded(): void {
    this.isQueryExpanded = !this.isQueryExpanded;
  }

  openGlossary(table: DataBaseTableItem): void {
    this.selectedTable = table;
    this.searchText = '';
    this.isGlossaryVisible = true;
  }

  closeGlossary(): void {
    this.isGlossaryVisible = false;
    this.selectedTable = null;
    this.searchText = '';
  }

  get filteredColumns(): MetadataColumnsType[] {
    const searchText = this.searchText.trim().toLowerCase();

    return (this.selectedTable?.columns ?? []).filter(
      (column) =>
        column.name?.toLowerCase().includes(searchText) || column.description?.toLowerCase().includes(searchText),
    );
  }

  get nameFormControl(): UntypedFormControl {
    return this.customQueryFormGroup.get('name') as UntypedFormControl;
  }

  get queryFormControl(): UntypedFormControl {
    return this.customQueryFormGroup.get('query') as UntypedFormControl;
  }

  get descriptionFormControl(): UntypedFormControl {
    return this.customQueryFormGroup.get('description') as UntypedFormControl;
  }

  set orgTypeFormControl(orgType: string) {
    this.customQueryFormGroup.get('orgType')?.setValue(orgType);
  }

  set createdByFormControl(createdBy: string) {
    this.customQueryFormGroup.get('createdBy')?.setValue(createdBy);
  }

  get orgIdFormControl(): UntypedFormControl {
    return this.customQueryFormGroup.get('orgId') as UntypedFormControl;
  }

  get isEditMode(): boolean {
    return !!this.queryId && !!this.executionId;
  }

  get isQueryProcessing(): boolean {
    return this.isLoading || this.isSubmittingQuery;
  }

  private async saveDraftAndSetIds(): Promise<boolean> {
    const { name, description, query } = this.customQueryFormGroup.value;

    if (this.customQueryFormGroup.invalid) {
      this.customQueryFormGroup.markAllAsTouched();
      this.messageService.warning('Please complete required fields before saving draft.');
      return false;
    }

    this.isSavingDraft = true;
    try {
      const response = await this.fanExplorationService.saveDraft({
        description,
        executionSource: ExecutionSource.WORKBENCH,
        executionId: this.executionId || undefined,
        name,
        queryId: this.queryId || undefined,
        query,
        queryType: QueryType.CUSTOM,
        validationLogs: this.validationLogs ? JSON.stringify(this.validationLogs) : undefined,
        validationStatus: this.validationStatus ?? undefined,
      });
      if (response?.queryId && response?.executionId) {
        this.queryId = response.queryId;
        this.executionId = response.executionId;
        this.messageService.success('Query saved successfully.');
        //this.validationLogs = 'Draft saved successfully.';
        return true;
      }
      this.messageService.error('Unable to save query.');
      return false;
    } catch (error) {
      console.error('Error saving query', error);
      this.messageService.error('Failed to save query. Please try again.');
      return false;
    } finally {
      this.isSavingDraft = false;
    }
  }

  async onSaveAsDraftClick(): Promise<void> {
    const isDraftSaved = await this.saveDraftAndSetIds();

    if (!this.isEditMode && isDraftSaved) {
      await this.navigateToEditPage();
    }
  }

  async onValidateAndRunClick(): Promise<void> {
    if (this.customQueryFormGroup.invalid) {
      this.customQueryFormGroup.markAllAsTouched();
      this.messageService.warning('Please complete required fields before validating and running query.');
      return;
    }

    this.isSubmittingQuery = true;

    try {
      if (!this.isEditMode) {
        const draftOk = await this.saveDraftAndSetIds();
        if (!draftOk) {
          this.messageService.error('Unable to save query draft before validating and running.');
          return;
        }
      }

      const response = await this.fanExplorationService.submitQuery({
        queryId: this.queryId,
        executionId: this.executionId,
      });

      if (response?.queryId && response?.executionId) {
        this.queryId = response.queryId;
        this.executionId = response.executionId;
        this.messageService.success('Query validated and submitted successfully.');
        //this.validationLogs = 'Query validated and submitted successfully.';
        this.historyTabRequested.emit();
        return;
      }

      this.messageService.error('Unable to validate and run query.');
    } catch (error) {
      console.error('Error validating and running query', error);
      this.messageService.error('Failed to validate and run query. Please try again.');
    } finally {
      this.isSubmittingQuery = false;
    }
  }

  private applyQueryRecord(): void {
    if (!this.queryRecord) {
      return;
    }

    this.customQueryFormGroup.patchValue(
      {
        name: this.queryRecord.name,
        description: this.queryRecord.description || '',
        query: this.queryRecord.query,
      },
      { emitEvent: false },
    );

    this.queryId = this.queryRecord.queryId || '';
    this.executionId = this.queryRecord.executionId || '';
    this.validationStatus = this.queryRecord.validationStatus ?? null;
    this.validationLogs = this.parseValidationLogs(this.queryRecord.validationLogs);
    this.checkValidationStatus = this.validationStatus === ValidationStatus.SUCCESS;
  }

  private parseValidationLogs(validationLogsRaw: string | null | undefined): ValidationResponse | undefined {
    if (!validationLogsRaw?.trim()) {
      return undefined;
    }

    try {
      return JSON.parse(validationLogsRaw) as ValidationResponse;
    } catch (error) {
      console.warn('Unable to parse validation logs', error);
      return undefined;
    }
  }

  private subscribeToMetadataSelectionChanges(): void {
    this.selectedDatasources.valueChanges.subscribe((datasources) => {
      const removedDatasources = this.previousDatasourcesValue.filter(
        (previousDatasource) => !datasources.includes(previousDatasource),
      );

      const removedDatabases = removedDatasources.flatMap(
        (removedDatasource) => this.dataSourceToDatabasesMap.get(removedDatasource) ?? [],
      );

      if (removedDatabases.length) {
        this.selectedDatabases.setValue(
          this.selectedDatabases.value.filter((database) => !removedDatabases.includes(database)),
        );
      }

      this.databaseList = Array.from(
        new Set(datasources.flatMap((datasource) => this.dataSourceToDatabasesMap.get(datasource) ?? [])),
      ).sort((left, right) => left.localeCompare(right));
      this.previousDatasourcesValue = datasources;
    });

    this.selectedDatabases.valueChanges.subscribe((databases) => {
      this.tableList = this.selectedDatasources.value
        .flatMap((datasource) => databases.flatMap((database) => this.tableMap.get(`${datasource}#${database}`) ?? []))
        .sort((left, right) => left.tableName.localeCompare(right.tableName));
    });
  }

  private async loadMetadata(): Promise<void> {
    this.isMetadataLoading = true;
    this.metadataErrorMessage = '';
    this.dataSourceList = [];
    this.databaseList = [];
    this.tableList = [];
    this.tableMap.clear();
    this.dataSourceToDatabasesMap.clear();
    this.selectedDatasources.setValue([]);
    this.selectedDatabases.setValue([]);
    this.previousDatasourcesValue = [];

    try {
      const metadataResponse = await this.fanExplorationService.getMetadata(this.orgId);
      const metadata = metadataResponse?.metadata ?? [];

      metadata.forEach((item) => this.addMetadataItem(item));

      this.dataSourceList = Array.from(new Set(metadata.map((item) => item.datasource_name?.trim() || '')))
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right));

      if (!this.dataSourceList.length) {
        this.metadataErrorMessage = this.noMetadataMessage;
      }
    } catch (error) {
      console.error('Error loading metadata', error);
      this.metadataErrorMessage = 'Unable to load metadata details.';
      this.messageService.error('Failed to load metadata details. Please try again.');
    } finally {
      this.isMetadataLoading = false;
    }
  }

  private addMetadataItem(item: Metadata): void {
    const datasourceName = item.datasource_name?.trim() || '';
    const databaseName = item.database_name?.trim() || '';
    const tableName = item.table_name?.trim() || '';

    if (!datasourceName || !databaseName || !tableName) {
      return;
    }

    this.addTableToMap(datasourceName, databaseName, {
      table_name: tableName,
      table_description: item.table_description,
      columns: item.columns ?? [],
    });
    this.addDatabaseToMap(datasourceName, databaseName);
  }

  private addTableToMap(
    dataSource: string,
    databaseName: string,
    item: { table_name: string; table_description?: string | null; columns?: MetadataColumnsType[] | null },
  ): void {
    const key = `${dataSource}#${databaseName}`;
    const tableItem: DataBaseTableItem = {
      datasourceName: dataSource,
      databaseName,
      tableDescription: item.table_description?.trim() || 'No table description available.',
      tableName: item.table_name,
      columns: item.columns ?? [],
    };
    const existingTableList = this.tableMap.get(key) ?? [];

    this.tableMap.set(key, [...existingTableList, tableItem]);
  }

  private addDatabaseToMap(dataSource: string, databaseName: string): void {
    const databaseList = this.dataSourceToDatabasesMap.get(dataSource) ?? [];

    if (!databaseList.includes(databaseName)) {
      this.dataSourceToDatabasesMap.set(dataSource, [...databaseList, databaseName]);
    }
  }

  async handleValidate(): Promise<void> {
    this.isLoading = true;
    this.spinningState = 'validate';
    this.isValidating = true;

    this.validationStatus = ValidationStatus.IN_PROGRESS;
    const isDraftSaved = await this.saveDraftAndSetIds();

    if (!isDraftSaved) {
      this.resetLoading();
      this.isValidating = false;
      return;
    }

    if (!this.isEditMode) {
      await this.navigateToEditPage();
    }

    await this.validateQueryRecordApi();
  }

  private async navigateToEditPage(): Promise<void> {
    if (!this.isEditMode) {
      return;
    }

    await this.router.navigate(['/market-fan-exploration/edit/CUSTOM', this.queryId, this.executionId]);
  }

  resetLoading(): void {
    this.isLoading = false;
    this.spinningState = null;
  }

  async validateQueryRecordApi(): Promise<void> {
    this.validationException = '';

    try {
      await this.apiService.query<ValidateQueryRecordMutation, ValidateQueryRecordMutationVariables>(
        ValidateQueryRecord,
        {
          queryRecord: {
            orgId: this.userManagementService.getOrgId(),
            query: this.queryFormControl.value,
            queryId: this.queryId,
            executionId: this.executionId,
            lastModifiedBy: this.userManagementService.getEmail(),
            validationStatus: ValidationStatus.IN_PROGRESS,
          },
        },
      );
      this.messageService.info(
        'Validation has started. This may take some time. You may continue working or navigate elsewhere.',
        { nzDuration: 8000 },
      );
    } catch (error: any) {
      this.messageService.error(`Unable to start validation: ${error}`);
    }
  }

  isClickableError(error: string | null): boolean {
    if (!error) return false;
    return error.includes('[ATHENA_ERROR]') && /line\s+(\d+):(\d+)/i.test(error);
  }

  listenRealTimeValidationChanges(orgId: string): void {
    this.graphqlValidationSubscription = this.apiService
      .subscription<
        OnValidateQueryRecordsSubscription,
        OnValidateQueryRecordsSubscriptionVariables
      >(onValidateQueryRecords, { orgId })
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onValidateQueryRecords;
          if (
            updatedItem &&
            updatedItem.validationStatus &&
            updatedItem.validationStatus !== ValidationStatus.IN_PROGRESS
          ) {
            this.updateStatusItem(updatedItem);
          }
        },
        error: (error: any) => {
          console.warn('Subscription error: ', error);
        },
      }) as Subscription;
  }

  updateStatusItem(updatedItem: QueryValidationRecord): void {
    if (this.queryId === updatedItem.queryId) {
      if (!this.isValidating) return;
      this.queryValidationRecord = {
        ...updatedItem,
      };
      this.validationStatus = updatedItem.validationStatus;
      this.validationLogs = this.parseValidationLogs(updatedItem.validationLogs);
      this.announceValidationStatusUpdate(updatedItem);
      if (updatedItem?.validationStatus === ValidationStatus.SUCCESS) {
        this.checkValidationStatus = true;
      } else if (updatedItem.validationStatus === ValidationStatus.FAIL) {
        this.checkValidationStatus = false;
      }
      this.resetLoading();
      this.isValidating = false;
    }
  }

  announceValidationStatusUpdate(newItem: QueryValidationRecord): void {
    const { name } = this.customQueryFormGroup.value;
    let msg = '';
    switch (newItem.validationStatus) {
      case ValidationStatus.SUCCESS: {
        msg = `Validation of Query ${name} is completed successfully.`;
        break;
      }

      case ValidationStatus.FAIL: {
        msg = `Validation of Query ${name} is failed.`;
        break;
      }
    }
    this.messageService.info(msg, {
      nzDuration: 5000,
    });
  }

  onAthenaErrorClick(err: any) {
    if (!err.includes('[ATHENA_ERROR]')) {
      return;
    }
  }

  private subscribeToQueryRecordFormChangeEvent() {
    this.customQueryFormGroup.valueChanges.subscribe(() => {
      if (this.validationStatus) {
        this.checkValidationStatus = false;
        this.validationLogs = undefined;
        this.validationException = undefined;
      }
    });
  }
}
